import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from './basicos';
import { empilharEsc } from './pilha-do-esc';
import { SelectDoSuite } from './seletores';
import { Icone } from './icones';

/**
 * Diálogo modal acessível: fecha no Esc e no clique fora, devolve o foco e
 * prende o foco dentro enquanto está aberto (documento 13.6).
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  onSubmit,
  submitLabel = 'Salvar',
  submitTone = 'primary',
  submitTravado = false,
  enviando = false,
  children,
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  submitLabel?: string;
  submitTone?: 'primary' | 'danger';
  /**
   * Botão de confirmação trancado até a condição bater (zona de perigo).
   *
   * É `aria-disabled`, e NÃO `disabled` puro - decisão da seção 8 de
   * `ROTAS-CONFIGURACOES.md`: `disabled` tira o botão da árvore de
   * acessibilidade, e aí quem usa leitor de tela não encontra o botão nem
   * ouve por que ele não responde. Com `aria-disabled` o botão continua
   * focável e anunciado, só não age - o envio é barrado no `onSubmit`.
   */
  submitTravado?: boolean;
  /**
   * Enviando (componentes.html §04.16): o botão primário entra em
   * `.carregando` e o resto do modal fica inerte até a resposta chegar.
   */
  enviando?: boolean;
  children?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  const tituloId = useId();
  const { montado, ligado } = useAberturaAnimada(open);

  /**
   * O fechar mora numa `ref`, e o efeito depende só de `open`.
   *
   * Com `onClose` na lista de dependências, o efeito refaz a cada render - e
   * quem chama passa quase sempre uma arrow nova (`onClose={() => setModal(null)}`),
   * então é a cada render mesmo. O problema não é o custo: é a LIMPEZA, que
   * devolve o foco para quem abriu. Num modal com campo controlado, cada tecla
   * digitada re-renderiza o pai, o efeito limpa, e o foco pula do campo de
   * volta para o botão que abriu o modal. Digitar vira impossível, e o defeito
   * só aparece nos formulários controlados, que são a maioria.
   */
  const fecharRef = useRef(onClose);
  fecharRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement;

    const desempilhar = empilharEsc(() => fecharRef.current());
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = ref.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    /* O primeiro campo, e não o primeiro focável: em ordem de documento o
       primeiro focável é o X de fechar, e abrir um formulário com o foco em
       "Fechar" convida a fechar. Sem campo, o botão seguro do rodapé. */
    const timer = window.setTimeout(() => {
      const alvo =
        ref.current?.querySelector<HTMLElement>('input, select, textarea') ??
        ref.current?.querySelector<HTMLElement>('.modal-actions button');
      alvo?.focus();
    }, 0);
    const soltar = travarFundo();

    return () => {
      desempilhar();
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(timer);
      soltar();
      restoreTo.current?.focus();
    };
  }, [open]);

  if (!montado) return null;

  return (
    <div
      className={`overlay${ligado ? ' on' : ''}`}
      onMouseDown={(e) => e.target === e.currentTarget && !enviando && onClose()}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        aria-busy={enviando || undefined}
        ref={ref}
      >
        {/* O X de 40px no canto (componentes.html §04.16, `.fechar-x`). */}
        <button type="button" className="fechar-x" onClick={onClose} disabled={enviando}>
          <Icone.Fechar titulo="Fechar" tamanho={18} />
        </button>
        <h3 id={tituloId}>{title}</h3>
        {description && <p className="desc-modal">{description}</p>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (submitTravado || enviando) return;
            onSubmit?.(e);
          }}
        >
          {/* `inert` deixa o miolo fora do alcance do teclado e do leitor de
              tela enquanto o envio roda; os campos param de responder sem
              perder o valor. */}
          <div className="stack modal__miolo" {...(enviando ? ({ inert: '' } as Record<string, string>) : {})}>
            {children}
          </div>
          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={onClose} disabled={enviando}>
              Cancelar
            </Button>
            {onSubmit && (
              <Button
                type="submit"
                variant={submitTone}
                loading={enviando}
                aria-disabled={submitTravado || undefined}
              >
                {submitLabel}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Confirmação destrutiva (F1, 26/10/2026).
 *
 * Toda exclusão do produto montava o seu `Modal` com `submitTone="danger"` e
 * um texto diferente, e algumas não tinham confirmação nenhuma. Esta é a
 * peça única: diz O QUE some e O QUE se perde junto, o botão diz o verbo
 * ("Excluir contato", nunca "Confirmar"), e o foco começa no Cancelar, que é
 * o lado seguro. Para o que não tem volta e pesa (organização, domínio,
 * caixa de e-mail), `confirmarCom` pede o nome digitado e o botão fica
 * trancado (`aria-disabled`) até bater.
 */
export function Confirmacao({
  open,
  titulo,
  texto,
  acao,
  confirmarCom,
  enviando = false,
  onConfirmar,
  onClose,
}: {
  open: boolean;
  titulo: string;
  /** O que acontece, em uma ou duas frases: o que some e o que vai junto. */
  texto: ReactNode;
  /** O verbo do botão: "Excluir contato", "Remover membro". */
  acao: string;
  /** Texto que a pessoa precisa digitar para destrancar (o nome do que some). */
  confirmarCom?: string;
  enviando?: boolean;
  onConfirmar: () => void;
  onClose: () => void;
}) {
  const [digitado, setDigitado] = useState('');
  useEffect(() => { if (!open) setDigitado(''); }, [open]);
  const travado = !!confirmarCom && digitado.trim() !== confirmarCom;
  const campoId = useId();
  return (
    <Modal
      open={open}
      title={titulo}
      onClose={onClose}
      onSubmit={() => onConfirmar()}
      submitLabel={acao}
      submitTone="danger"
      submitTravado={travado}
      enviando={enviando}
    >
      <div className="confirmacao">
        <span className="confirmacao__icone" aria-hidden="true"><Icone.AlertaTriangulo tamanho={20} /></span>
        <div className="confirmacao__texto">{texto}</div>
      </div>
      {confirmarCom && (
        <div className="field">
          <label htmlFor={campoId}>Para confirmar, digite <strong>{confirmarCom}</strong></label>
          <input
            id={campoId}
            className="input"
            autoComplete="off"
            spellCheck={false}
            value={digitado}
            onChange={(e) => setDigitado(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}

/**
 * A remoção de um item de lista, já ligada a uma mutação (nasceu no MKT, F6;
 * subiu para o kit na F1, rodada 3). Recebe o alvo (`null` fecha), o texto a
 * partir do nome e a mutação; fecha quando o banco responde, e o erro, se
 * houver, segue para onde a tela já o mostra.
 */
export function ConfirmarRemocao({
  alvo,
  titulo,
  acao,
  texto,
  remover,
  aoFechar,
}: {
  alvo: { id: string; nome: string } | null;
  titulo: string;
  acao: string;
  texto: (nome: string) => ReactNode;
  remover: { mutate: (id: string, o?: { onSettled?: () => void }) => void; isPending: boolean };
  aoFechar: () => void;
}) {
  /* O último alvo fica guardado enquanto o diálogo sai, para o texto não
     sumir no meio da animação de saída. */
  const ultimo = useRef(alvo);
  if (alvo) ultimo.current = alvo;
  const mostrado = alvo ?? ultimo.current;
  return (
    <Confirmacao
      open={alvo !== null}
      titulo={titulo}
      acao={acao}
      texto={mostrado ? texto(mostrado.nome) : null}
      enviando={remover.isPending}
      onConfirmar={() => alvo && remover.mutate(alvo.id, { onSettled: aoFechar })}
      onClose={aoFechar}
    />
  );
}

type PedidoDeConfirmacao = {
  titulo: string;
  texto: ReactNode;
  acao: string;
  confirmarCom?: string;
};

/**
 * Confirmação numa linha, com promessa (F1, rodada 3):
 *
 *   const { confirmar, dialogo } = useConfirmar();
 *   if (await confirmar({ titulo: 'Excluir a regra?', texto: ..., acao: 'Excluir regra' })) remover();
 *   ...
 *   return <>{...}{dialogo}</>;
 *
 * Resolve `true` no botão de ação e `false` no Cancelar, no Esc e no clique
 * fora. Para remoção ligada a uma mutação com "enviando", `ConfirmarRemocao`.
 */
export function useConfirmar() {
  const [pedido, setPedido] = useState<PedidoDeConfirmacao | null>(null);
  const resolver = useRef<((sim: boolean) => void) | null>(null);
  const ultimo = useRef<PedidoDeConfirmacao | null>(null);
  if (pedido) ultimo.current = pedido;
  const confirmar = useCallback((p: PedidoDeConfirmacao) => {
    resolver.current?.(false);
    return new Promise<boolean>((r) => {
      resolver.current = r;
      setPedido(p);
    });
  }, []);
  const fechar = useCallback((sim: boolean) => {
    resolver.current?.(sim);
    resolver.current = null;
    setPedido(null);
  }, []);
  const mostrado = pedido ?? ultimo.current;
  const dialogo = (
    <Confirmacao
      open={pedido !== null}
      titulo={mostrado?.titulo ?? ''}
      texto={mostrado?.texto ?? null}
      acao={mostrado?.acao ?? 'Confirmar'}
      confirmarCom={mostrado?.confirmarCom}
      onConfirmar={() => fechar(true)}
      onClose={() => fechar(false)}
    />
  );
  return { confirmar, dialogo };
}

/**
 * Abrir e fechar com movimento (movimento.html §06.5, "4. Abrir de modal").
 *
 * Com `open` virando `true` o elemento monta e ganha `.on` no quadro
 * seguinte: montar já com a classe faz o navegador fundir o estado inicial
 * com o final e a transição nunca roda. Com `open` virando `false` a classe
 * sai primeiro e o elemento só desmonta depois de `--d-curta` (240ms), para
 * a saída acontecer de verdade em vez de cortar seco.
 */
export function useAberturaAnimada(open: boolean, saidaMs = 240) {
  const [montado, setMontado] = useState(open);
  const [ligado, setLigado] = useState(false);
  useEffect(() => {
    if (open) {
      setMontado(true);
      const quadro = requestAnimationFrame(() => setLigado(true));
      return () => cancelAnimationFrame(quadro);
    }
    setLigado(false);
    const relogio = window.setTimeout(() => setMontado(false), saidaMs);
    return () => window.clearTimeout(relogio);
  }, [open, saidaMs]);
  return { montado, ligado };
}

/**
 * Trava a rolagem da página enquanto uma sobreposição está aberta
 * (componentes.html §04.16 e §04.17). Conta as sobreposições abertas para
 * uma gaveta fechando por cima de um modal não destravar o fundo cedo.
 */
let sobreposicoesAbertas = 0;
export function travarFundo() {
  if (typeof document === 'undefined') return () => {};
  sobreposicoesAbertas += 1;
  if (sobreposicoesAbertas === 1) document.body.style.overflow = 'hidden';
  return () => {
    sobreposicoesAbertas = Math.max(0, sobreposicoesAbertas - 1);
    if (sobreposicoesAbertas === 0) document.body.style.overflow = '';
  };
}

/**
 * Campo de seleção com o mesmo formato do Field - botão + lista suspensa
 * própria do design system, nunca `<select>` cru (mesma regra do seletor de
 * dispositivo da sala de reunião, `SeletorDeDispositivo` em
 * `pages/reuniao/Reuniao.tsx`): a lista de opções de um `<select>` nativo é
 * desenhada pelo sistema operacional, não pelo yard.css, e por isso nunca
 * bate com o resto da interface. Aqui a lista é HTML normal, então carrega
 * `.select-custom` inteiro.
 *
 * `aria-haspopup="listbox"`, fecha por Esc, clique fora ou ao escolher;
 * devolve o foco ao botão que abriu. Setas Cima/Baixo andam entre opções,
 * Home/End vão para a primeira/última, Enter e Espaço escolhem.
 */
export function SelectField({
  label,
  value,
  onChange,
  options,
  help,
  id: idProp,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  help?: string;
  /** Sobrescreve o id derivado do label - obrigatório quando o campo se repete numa lista (ex: um SelectField "Tipo" por item), senão dois itens dividem o mesmo id e o mesmo `<label for>`. */
  id?: string;
  disabled?: boolean;
}) {
  const id = idProp ?? `s-${label.toLowerCase().replace(/\W+/g, '-')}`;
  return <div className="field"><label htmlFor={id}>{label}</label><SelectDoSuite id={id} value={value} disabled={disabled} aria-describedby={help ? `${id}-help` : undefined} onChange={e => onChange(e.target.value)}>{options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</SelectDoSuite>{help && <span className="help" id={`${id}-help`}>{help}</span>}</div>;
}
